import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@marineform/fe-util';
import { Observable, of } from 'rxjs';
import {
  FEATURE_FLAGS,
  FeatureFlag,
  FeatureFlags,
} from '../feature-flags/featureFlags';

const STORAGE_KEY = 'fe.featureFlagOverrides';
const FEATURE_FLAG_VALUES = new Set<string>(Object.values(FeatureFlag));

export type FeatureFlagOverrides = Partial<FeatureFlags>;

export interface FeatureFlagsSnapshot {
  flags: FeatureFlags;
  overrides: FeatureFlagOverrides;
}

function readStoredOverrides(
  storage: LocalStorageService,
): FeatureFlagOverrides {
  try {
    const stored = storage.get<Partial<Record<string, unknown>>>(STORAGE_KEY);
    if (stored == null) {
      return {};
    }

    const overrides: FeatureFlagOverrides = {};
    for (const [key, value] of Object.entries(stored)) {
      if (FEATURE_FLAG_VALUES.has(key) && typeof value === 'boolean') {
        overrides[key as FeatureFlag] = value;
      }
    }
    return overrides;
  } catch {
    return {};
  }
}

function mergeFlags(overrides: FeatureFlagOverrides): FeatureFlags {
  return { ...FEATURE_FLAGS, ...overrides };
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  #storage = inject(LocalStorageService);

  getFeatureFlags(): Observable<FeatureFlags> {
    return of(this.getSnapshot().flags);
  }

  getSnapshot(): FeatureFlagsSnapshot {
    const overrides = readStoredOverrides(this.#storage);
    return {
      flags: mergeFlags(overrides),
      overrides,
    };
  }

  setOverride(flag: FeatureFlag, enabled: boolean): FeatureFlagsSnapshot {
    const overrides = {
      ...readStoredOverrides(this.#storage),
      [flag]: enabled,
    };
    this.#storage.set(STORAGE_KEY, overrides);
    return {
      flags: mergeFlags(overrides),
      overrides,
    };
  }

  clearOverrides(): FeatureFlagsSnapshot {
    this.#storage.remove(STORAGE_KEY);
    return {
      flags: FEATURE_FLAGS,
      overrides: {},
    };
  }
}
